import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import api from '../lib/api';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';
import { Checkbox } from './ui/checkbox';
import { SearchableCombobox } from './ui/searchable-combobox';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from './ui/dialog';
import { toast } from 'sonner';
import {
  AlertTriangle, Clock, Play, Pencil, TimerReset, Lock, History,
  Loader2, Save, X, Ban,
} from 'lucide-react';

const fmtCLP = (n) => (Number(n) || 0).toLocaleString('es-CL');

/**
 * MaterialDescripcionCombobox — Iter 291.p.3 (Carlos)
 *
 * Restringido a materiales del catálogo (sin texto libre). El usuario
 * escribe en el input para filtrar y DEBE seleccionar una opción del
 * combobox. Al seleccionar, autocompleta `codigo` + `descripcion` en la fila.
 * El input no persiste texto libre — solo sirve como caja de búsqueda.
 */
function MaterialDescripcionCombobox({ codigo, descripcion, onSelect, testid, disabled }) {
  const [q, setQ] = useState('');
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const debounceRef = useRef(null);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!q || q.length < 2) { setItems([]); return; }
    debounceRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        const res = await api.get('/bodega/catalogo-materiales', { params: { q, limit: 40 } });
        setItems((res.data?.materiales || []).map(m => ({
          value: m.codigo,
          label: (m.descripcion || m.descripcion2 || m.codigo || '').trim(),
          subLabel: `${m.codigo || ''}${m.descripcion2 && m.descripcion2 !== m.descripcion ? ' · ' + m.descripcion2 : ''}`,
          _material: m,
        })));
      } catch {
        setItems([]);
      } finally { setLoading(false); }
    }, 300);
    return () => debounceRef.current && clearTimeout(debounceRef.current);
  }, [q]);

  // Incluir el material actual como opción "fantasma" solo si viene del catálogo
  // (para que se vea seleccionado mientras el usuario no filtra).
  const valueOptions = useMemo(() => {
    const base = items;
    if (codigo && !base.find(i => String(i.value) === String(codigo))) {
      return [{ value: codigo, label: descripcion || codigo, subLabel: codigo, _current: true }, ...base];
    }
    return base;
  }, [items, codigo, descripcion]);

  const currentLabel = descripcion || (codigo ? `[${codigo}]` : '');

  return (
    <div className="flex flex-col gap-1">
      {codigo ? (
        <div className="text-[11px] px-1.5 py-1 rounded border bg-slate-50 border-slate-300" title={codigo} data-testid={`${testid}-actual`}>
          <span className="font-medium">{currentLabel}</span>
          <span className="font-mono text-slate-500 ml-1">({codigo})</span>
        </div>
      ) : (
        <div className="text-[11px] px-1.5 py-1 rounded border-2 border-dashed border-red-300 bg-red-50 text-red-700 italic" data-testid={`${testid}-vacio`}>
          Sin material seleccionado — usá el buscador
        </div>
      )}
      <Input
        value={q}
        onChange={(e) => setQ(e.target.value)}
        placeholder="Buscar en catálogo… (mín. 2 letras)"
        className="text-xs h-7"
        disabled={disabled}
        data-testid={`${testid}-search`}
      />
      {q.length >= 2 && (
        <SearchableCombobox
          items={valueOptions.filter(o => !o._current)}
          value={codigo}
          onChange={(v) => {
            const m = valueOptions.find(x => String(x.value) === String(v));
            if (m && !m._current) {
              onSelect({ codigo: m.value, descripcion: m.label });
              setQ('');
            }
          }}
          placeholder={loading ? 'Buscando…' : 'Elegir del catálogo (obligatorio)'}
          emptyMessage="Sin resultados en catálogo"
          className="text-xs"
          testid={testid}
        />
      )}
    </div>
  );
}

/** Iter 291.p — Panel único de "Regularización de Recepción" post-Alegra.
 *  Permite a admin/gerencia bypassear el bloqueo de recepciones cuyo DTE ya
 *  cerró ciclo en Alegra, en una ventana temporal (24h × 5 extensiones).
 */
export default function RegularizacionRecepcionModal({ open, recepcion, onClose, onChanged }) {
  const [rec, setRec] = useState(null);
  const [detalle, setDetalle] = useState([]);
  const [historial, setHistorial] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showHistorial, setShowHistorial] = useState(false);

  // Iniciar
  const [motivoInicio, setMotivoInicio] = useState('');
  const [iniciando, setIniciando] = useState(false);

  // Extender
  const [showExtender, setShowExtender] = useState(false);
  const [motivoExtender, setMotivoExtender] = useState('');
  const [extendiendo, setExtendiendo] = useState(false);

  // Editar
  const [showEditar, setShowEditar] = useState(false);
  const [items, setItems] = useState([]);
  const [motivoEdicion, setMotivoEdicion] = useState('');
  const [aplicarStock, setAplicarStock] = useState(false);
  const [confirmandoStock, setConfirmandoStock] = useState(false);
  const [guardando, setGuardando] = useState(false);

  // Cerrar
  const [cerrando, setCerrando] = useState(false);

  const load = useCallback(async () => {
    if (!recepcion?.numero_recepcion) return;
    setLoading(true);
    try {
      const [rDet, rHist] = await Promise.all([
        api.get(`/bodega/recepciones/${recepcion.numero_recepcion}/items`).catch(() => ({ data: { items: [] } })),
        api.get(`/bodega/recepciones/${recepcion.numero_recepcion}/regularizacion/historial`),
      ]);
      setDetalle(rDet.data?.items || []);
      setHistorial(rHist.data?.items || []);
      // Refresh recepcion state (server-side flags)
      try {
        const rR = await api.get(`/compras/recepciones?q=${recepcion.numero_recepcion}&limit=1`);
        const found = (rR.data?.recepciones || []).find(x => x.numero_recepcion === recepcion.numero_recepcion);
        setRec(found || recepcion);
      } catch { setRec(recepcion); }
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Error al cargar recepción');
    } finally {
      setLoading(false);
    }
  }, [recepcion]);

  useEffect(() => {
    if (open) {
      setShowHistorial(false);
      setShowExtender(false);
      setShowEditar(false);
      setMotivoInicio('');
      setMotivoExtender('');
      setMotivoEdicion('');
      setAplicarStock(false);
      setConfirmandoStock(false);
      load();
    }
  }, [open, load]);

  const activa = !!rec?.regularizacion_abierta;
  const expiraAt = rec?.regularizacion_expira_at ? new Date(rec.regularizacion_expira_at) : null;
  const now = new Date();
  const vencida = expiraAt ? expiraAt <= now : false;
  const horasRestantes = expiraAt ? Math.max(0, (expiraAt - now) / 3600000) : 0;
  const extensiones = rec?.regularizacion_extensiones || 0;
  const MAX_EXT = 5;

  const iniciarItems = () => {
    setItems(detalle.map(d => ({
      item: d.item,
      cantidad_recepcionada: d.cantidad_recepcionada || 0,
      valor_unitario: d.valor_unitario || 0,
      codigo_material: d.codigo_material || '',
      descripcion: d.descripcion || '',
      ot_numero: d.ot_numero || 0,
      anular_item: false,
      _prev_qty: d.cantidad_recepcionada || 0,
      _prev_vu: d.valor_unitario || 0,
    })));
    setMotivoEdicion('');
    setAplicarStock(false);
    setConfirmandoStock(false);
    setShowEditar(true);
  };

  const handleIniciar = async () => {
    if ((motivoInicio || '').trim().length < 5) {
      toast.error('El motivo debe tener al menos 5 caracteres');
      return;
    }
    setIniciando(true);
    try {
      const res = await api.post(
        `/bodega/recepciones/${recepcion.numero_recepcion}/regularizacion/iniciar`,
        { motivo: motivoInicio.trim() }
      );
      toast.success(res.data?.message || 'Regularización abierta');
      setMotivoInicio('');
      await load();
      onChanged?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Error al iniciar regularización');
    } finally { setIniciando(false); }
  };

  const handleExtender = async () => {
    if ((motivoExtender || '').trim().length < 5) {
      toast.error('El motivo debe tener al menos 5 caracteres');
      return;
    }
    setExtendiendo(true);
    try {
      const res = await api.post(
        `/bodega/recepciones/${recepcion.numero_recepcion}/regularizacion/extender`,
        { motivo: motivoExtender.trim() }
      );
      toast.success(res.data?.message || 'Ventana extendida');
      setMotivoExtender('');
      setShowExtender(false);
      await load();
      onChanged?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Error al extender ventana');
    } finally { setExtendiendo(false); }
  };

  const handleCerrar = async () => {
    if (!window.confirm('¿Confirma cerrar la regularización? Los cambios ya realizados quedan permanentes.')) return;
    setCerrando(true);
    try {
      const res = await api.post(`/bodega/recepciones/${recepcion.numero_recepcion}/regularizacion/cerrar`);
      toast.success(res.data?.message || 'Regularización cerrada');
      await load();
      onChanged?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Error al cerrar regularización');
    } finally { setCerrando(false); }
  };

  const stockDeltas = useMemo(() => items
    .filter(it => it.cantidad_recepcionada !== it._prev_qty)
    .map(it => ({
      item: it.item,
      codigo: it.codigo_material,
      delta: (it.anular_item ? 0 : Number(it.cantidad_recepcionada || 0)) - Number(it._prev_qty || 0),
    })), [items]);

  const handleGuardarEdicion = async () => {
    if ((motivoEdicion || '').trim().length < 5) {
      toast.error('El motivo debe tener al menos 5 caracteres');
      return;
    }
    // Iter 291.p.3 — Todos los ítems activos deben tener código del catálogo
    const sinCodigo = items.filter(it => !it.anular_item && !(it.codigo_material || '').trim());
    if (sinCodigo.length > 0) {
      toast.error(`Ítem${sinCodigo.length > 1 ? 's' : ''} ${sinCodigo.map(x => x.item).join(', ')} sin material del catálogo. Seleccioná uno o marca "Anular".`);
      return;
    }
    // Si se pide ajustar stock, obligar confirmación previa
    if (aplicarStock && !confirmandoStock) {
      setConfirmandoStock(true);
      return;
    }
    setGuardando(true);
    try {
      const res = await api.put(
        `/bodega/recepciones/${recepcion.numero_recepcion}/regularizacion/editar`,
        {
          items: items.map(it => ({
            item: it.item,
            cantidad_recepcionada: it.anular_item ? 0 : Number(it.cantidad_recepcionada || 0),
            valor_unitario: Number(it.valor_unitario || 0),
            codigo_material: it.codigo_material,
            descripcion: it.descripcion,
            ot_numero: it.ot_numero ? Number(it.ot_numero) : 0,
            anular_item: !!it.anular_item,
          })),
          motivo: motivoEdicion.trim(),
          aplicar_ajuste_stock: !!aplicarStock,
        }
      );
      toast.success(res.data?.message || 'Ítems actualizados');
      setShowEditar(false);
      setAplicarStock(false);
      setConfirmandoStock(false);
      await load();
      onChanged?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Error al guardar cambios');
    } finally { setGuardando(false); }
  };

  const updateItem = (idx, patch) => {
    setItems(prev => prev.map((it, i) => i === idx ? { ...it, ...patch } : it));
  };

  const renderCountdown = () => {
    if (!activa) return null;
    const label = vencida
      ? <Badge variant="destructive" className="gap-1"><AlertTriangle className="w-3.5 h-3.5" /> Ventana VENCIDA</Badge>
      : <Badge className="gap-1 bg-amber-500 hover:bg-amber-600"><Clock className="w-3.5 h-3.5" /> {horasRestantes.toFixed(1)}h restantes</Badge>;
    return (
      <div className="bg-amber-50 border-2 border-amber-300 rounded p-3 text-sm space-y-2" data-testid="regularizacion-panel-activa">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="font-semibold text-amber-900">Regularización activa</span>
            {label}
          </div>
          <span className="text-xs text-amber-700">Extensiones: {extensiones}/{MAX_EXT}</span>
        </div>
        <div className="text-xs text-amber-800">
          <strong>Iniciada:</strong> {rec.regularizacion_iniciada_por_nombre || rec.regularizacion_iniciada_por}
          {' · '}{new Date(rec.regularizacion_iniciada_at).toLocaleString('es-CL')}
        </div>
        {rec.regularizacion_motivo_inicial && (
          <div className="text-xs text-amber-800 italic">&ldquo;{rec.regularizacion_motivo_inicial}&rdquo;</div>
        )}
        <div className="text-xs text-amber-800">
          <strong>Vence:</strong> {expiraAt?.toLocaleString('es-CL')}
        </div>
      </div>
    );
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose?.(); }}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="dialog-regularizacion">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-amber-800">
            <AlertTriangle className="w-5 h-5" /> Regularización de Recepción
          </DialogTitle>
          <DialogDescription>
            Bypass temporal (24h × 5 ext.) para corregir ingresos aún cuando la factura cerró ciclo en Alegra.
            Solo Admin/Gerencia. Historial completo y aviso a Gerencia si no se cierra a tiempo.
          </DialogDescription>
        </DialogHeader>

        {loading && (
          <div className="flex items-center justify-center py-8 text-muted-foreground text-sm">
            <Loader2 className="w-5 h-5 animate-spin mr-2" /> Cargando…
          </div>
        )}

        {rec && !loading && (
          <div className="space-y-4">
            <div className="bg-gray-50 rounded p-3 text-sm grid grid-cols-2 md:grid-cols-4 gap-2">
              <div><strong>Recepción:</strong> <span className="font-mono">{rec.numero_recepcion}</span></div>
              <div><strong>OC:</strong> {rec.numero_oc || '—'}</div>
              <div><strong>Doc:</strong> {(rec.tipo_documento || '').toUpperCase()} {rec.docmto || ''}</div>
              <div><strong>Monto:</strong> ${fmtCLP(Math.round(rec.monto || 0))}</div>
            </div>

            {renderCountdown()}

            {/* Estado: no activa → mostrar form iniciar */}
            {!activa && (
              <div className="border rounded p-4 space-y-3" data-testid="regularizacion-form-iniciar">
                <div className="flex items-start gap-2 text-sm">
                  <Play className="w-4 h-4 text-emerald-600 mt-0.5" />
                  <div className="flex-1">
                    <p className="font-semibold">Iniciar regularización</p>
                    <p className="text-xs text-muted-foreground">Se abrirá una ventana de 24 h (extensible +24h × 5 = 6 días máx). Al cerrarla, los cambios quedan permanentes.</p>
                  </div>
                </div>
                <div>
                  <Label className="text-xs">Motivo * (mín. 5 caracteres)</Label>
                  <Textarea
                    value={motivoInicio}
                    onChange={(e) => setMotivoInicio(e.target.value)}
                    placeholder="Ej: Corregir cantidad ingresada erróneamente en el ítem 1 (3.28E15 en vez de 3.28)"
                    className="text-sm mt-1"
                    rows={2}
                    data-testid="input-motivo-inicio"
                  />
                </div>
                <div className="flex justify-end">
                  <Button
                    onClick={handleIniciar}
                    disabled={iniciando || (motivoInicio || '').trim().length < 5}
                    className="bg-amber-600 hover:bg-amber-700"
                    data-testid="btn-confirmar-iniciar">
                    {iniciando ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Play className="w-4 h-4 mr-2" />}
                    Abrir regularización
                  </Button>
                </div>
              </div>
            )}

            {/* Estado: activa → mostrar acciones */}
            {activa && !showEditar && !showExtender && (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                <Button
                  onClick={iniciarItems}
                  className="bg-blue-600 hover:bg-blue-700"
                  disabled={vencida}
                  data-testid="btn-editar-items">
                  <Pencil className="w-4 h-4 mr-2" /> Editar ítems
                </Button>
                <Button
                  onClick={() => setShowExtender(true)}
                  variant="outline"
                  disabled={extensiones >= MAX_EXT}
                  data-testid="btn-extender">
                  <TimerReset className="w-4 h-4 mr-2" /> Extender +24h
                </Button>
                <Button
                  onClick={handleCerrar}
                  disabled={cerrando}
                  className="bg-emerald-600 hover:bg-emerald-700"
                  data-testid="btn-cerrar">
                  {cerrando ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Lock className="w-4 h-4 mr-2" />}
                  Cerrar
                </Button>
                <Button
                  onClick={() => setShowHistorial(!showHistorial)}
                  variant="outline"
                  data-testid="btn-toggle-historial">
                  <History className="w-4 h-4 mr-2" /> Historial ({historial.length})
                </Button>
              </div>
            )}

            {/* Form extender */}
            {showExtender && (
              <div className="border rounded p-4 space-y-3">
                <p className="font-semibold text-sm flex items-center gap-2"><TimerReset className="w-4 h-4" /> Extender ventana +24h</p>
                <div>
                  <Label className="text-xs">Motivo *</Label>
                  <Textarea
                    value={motivoExtender}
                    onChange={(e) => setMotivoExtender(e.target.value)}
                    placeholder="Motivo de la extensión"
                    className="text-sm mt-1"
                    rows={2}
                    data-testid="input-motivo-extender"
                  />
                </div>
                <div className="flex justify-end gap-2">
                  <Button variant="outline" onClick={() => setShowExtender(false)} disabled={extendiendo}>Cancelar</Button>
                  <Button onClick={handleExtender} disabled={extendiendo || (motivoExtender || '').trim().length < 5}>
                    {extendiendo ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <TimerReset className="w-4 h-4 mr-2" />}
                    Extender
                  </Button>
                </div>
              </div>
            )}

            {/* Form editar */}
            {showEditar && (
              <div className="border rounded p-4 space-y-3">
                <p className="font-semibold text-sm flex items-center gap-2"><Pencil className="w-4 h-4" /> Editar ítems de la recepción</p>
                <div className="overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead className="bg-muted">
                      <tr>
                        <th className="p-1.5 text-left">#</th>
                        <th className="p-1.5 text-left">Descripción</th>
                        <th className="p-1.5 text-left">Código</th>
                        <th className="p-1.5 text-right">Cantidad</th>
                        <th className="p-1.5 text-right">Valor unit.</th>
                        <th className="p-1.5 text-right">Total</th>
                        <th className="p-1.5 text-center">Anular</th>
                      </tr>
                    </thead>
                    <tbody>
                      {items.map((it, i) => (
                        <tr key={i} className={`border-t ${it.anular_item ? 'bg-red-50 opacity-70' : ''}`}>
                          <td className="p-1.5">{it.item}</td>
                          <td className="p-1.5 min-w-[260px]">
                            <MaterialDescripcionCombobox
                              codigo={it.codigo_material}
                              descripcion={it.descripcion}
                              disabled={it.anular_item}
                              onSelect={({ codigo, descripcion }) => updateItem(i, {
                                codigo_material: codigo,
                                descripcion: descripcion,
                              })}
                              testid={`reg-item-desc-combo-${i}`}
                            />
                          </td>
                          <td className="p-1.5">
                            {/* Iter 291.p.3 — Código es solo lectura (deriva del combobox de descripción) */}
                            <div className="text-xs font-mono px-2 py-1 rounded bg-slate-100 border border-slate-200" data-testid={`reg-item-cod-${i}`}>
                              {it.codigo_material || <span className="text-red-500 italic">—</span>}
                            </div>
                          </td>
                          <td className="p-1.5">
                            <Input type="number" step="0.01" value={it.cantidad_recepcionada} onChange={(e) => updateItem(i, { cantidad_recepcionada: e.target.value })} className="text-xs h-7 text-right" disabled={it.anular_item} data-testid={`reg-item-qty-${i}`} />
                          </td>
                          <td className="p-1.5">
                            <Input type="number" step="0.01" value={it.valor_unitario} onChange={(e) => updateItem(i, { valor_unitario: e.target.value })} className="text-xs h-7 text-right" disabled={it.anular_item} data-testid={`reg-item-vu-${i}`} />
                          </td>
                          <td className="p-1.5 text-right font-mono">${fmtCLP(Math.round((it.anular_item ? 0 : Number(it.cantidad_recepcionada || 0)) * Number(it.valor_unitario || 0)))}</td>
                          <td className="p-1.5 text-center">
                            <Checkbox
                              checked={it.anular_item}
                              onCheckedChange={(v) => updateItem(i, { anular_item: !!v })}
                              data-testid={`reg-item-anular-${i}`}
                            />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <div className="flex items-center gap-2 border-t pt-3">
                  <Checkbox id="aplicar-stock" checked={aplicarStock} onCheckedChange={(v) => { setAplicarStock(!!v); setConfirmandoStock(false); }} data-testid="chk-aplicar-stock" />
                  <Label htmlFor="aplicar-stock" className="text-xs cursor-pointer">
                    Aplicar ajuste de stock (mueve las cantidades en materiales)
                  </Label>
                </div>

                {stockDeltas.length > 0 && aplicarStock && (
                  <div className="bg-yellow-50 border border-yellow-300 rounded p-2 text-xs">
                    <strong>Ajustes de stock a aplicar:</strong>
                    <ul className="list-disc pl-4 mt-1">
                      {stockDeltas.map((d, i) => (
                        <li key={i}>{d.codigo || '(sin código)'}: {d.delta > 0 ? '+' : ''}{d.delta}</li>
                      ))}
                    </ul>
                  </div>
                )}

                <div>
                  <Label className="text-xs">Motivo * (mín. 5 caracteres)</Label>
                  <Textarea value={motivoEdicion} onChange={(e) => setMotivoEdicion(e.target.value)} placeholder="Ej: Cantidad correcta según guía física" className="text-sm mt-1" rows={2} data-testid="input-motivo-edicion" />
                </div>

                {confirmandoStock && (
                  <div className="bg-red-50 border-2 border-red-300 rounded p-3 text-xs text-red-900">
                    ⚠ Estás por <strong>modificar el stock físico</strong> de {stockDeltas.length} material(es). ¿Confirmas?
                  </div>
                )}

                <div className="flex justify-end gap-2">
                  <Button variant="outline" onClick={() => { setShowEditar(false); setConfirmandoStock(false); }} disabled={guardando}>
                    <X className="w-4 h-4 mr-1" /> Cancelar
                  </Button>
                  <Button
                    onClick={handleGuardarEdicion}
                    disabled={guardando || (motivoEdicion || '').trim().length < 5}
                    className={confirmandoStock ? 'bg-red-600 hover:bg-red-700' : 'bg-blue-600 hover:bg-blue-700'}
                    data-testid="btn-guardar-edicion">
                    {guardando ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                    {confirmandoStock ? 'CONFIRMAR y aplicar' : 'Guardar cambios'}
                  </Button>
                </div>
              </div>
            )}

            {/* Historial */}
            {showHistorial && (
              <div className="border rounded p-3">
                <p className="text-xs font-semibold mb-2 flex items-center gap-2"><History className="w-4 h-4" /> Historial de regularización</p>
                {historial.length === 0 ? (
                  <p className="text-xs text-muted-foreground italic">Sin eventos registrados.</p>
                ) : (
                  <div className="max-h-64 overflow-y-auto space-y-2">
                    {historial.map((h, i) => (
                      <div key={i} className="border-l-2 border-amber-500 pl-2 py-1 text-xs" data-testid={`hist-entry-${i}`}>
                        <div className="flex items-center justify-between gap-2">
                          <div className="flex items-center gap-1.5">
                            <Badge variant={h.accion === 'expirar' ? 'destructive' : h.accion === 'cerrar' ? 'default' : 'secondary'} className="text-[10px]">
                              {h.accion}
                            </Badge>
                            <span className="font-mono text-[10px] text-muted-foreground">{new Date(h.timestamp).toLocaleString('es-CL')}</span>
                          </div>
                          <span className="text-[10px] text-muted-foreground">{h.usuario_nombre || h.usuario_email}</span>
                        </div>
                        {h.motivo && <p className="italic text-[11px] text-gray-700 mt-0.5">&ldquo;{h.motivo}&rdquo;</p>}
                        {Array.isArray(h.cambios) && h.cambios.length > 0 && (
                          <ul className="mt-1 space-y-0.5 pl-2">
                            {h.cambios.map((c, j) => (
                              <li key={j} className="text-[11px]">
                                <span className="font-mono font-semibold">Ítem {c.item}:</span>{' '}
                                {(c.cambios || []).map((cc, k) => (
                                  <span key={k} className="mr-2">
                                    <em>{cc.campo}</em>: <span className="text-red-600 line-through">{String(cc.antes)}</span> → <span className="text-emerald-600">{String(cc.despues)}</span>
                                  </span>
                                ))}
                              </li>
                            ))}
                          </ul>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={onClose}><Ban className="w-4 h-4 mr-1" /> Cerrar</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
